import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useShortcutsHelp } from "@/shared/lib/hooks/useShortcutsHelp";
import { formatChord, formatShortcut } from "@/shared/lib/shortcut";
import { Kbd } from "./Kbd";
import { Surface } from "./Surface";
import { Text } from "./Text";

/** A single keyboard shortcut shown in the help overlay. Either a combo
 *  (`shortcut`, e.g. `"mod+k"`) or a leader chord (`chord`, e.g. `"g i"`). */
export interface ShortcutHint {
  /** Combo string, e.g. `"mod+shift+o"` — rendered via `formatShortcut`. */
  shortcut?: string;
  /** Leader chord, e.g. `"g i"` (press in sequence) — rendered via `formatChord`. */
  chord?: string;
  /** Already-translated description of what the shortcut does. */
  label: string;
}

export interface ShortcutsDialogProps {
  /** Shortcuts to list, in display order. */
  hints: ShortcutHint[];
}

/**
 * The keyboard-shortcuts help sheet (portal + ink scrim + paper card). Lists
 * every global shortcut with its keycaps, rendered through `formatShortcut` so
 * the combos match the user's platform (⌘ vs Ctrl). Open state lives in a tiny
 * module store (`useShortcutsHelp`) so any control or shortcut can summon it.
 * Mount once in the app shell.
 */
export function ShortcutsDialog({ hints }: ShortcutsDialogProps) {
  const { t } = useTranslation();
  const { open, setOpen } = useShortcutsHelp();

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, setOpen]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label={t("shortcuts.title")}
    >
      <button
        type="button"
        aria-label={t("shortcuts.close")}
        onClick={() => setOpen(false)}
        className="absolute inset-0 cursor-default bg-ink/30 backdrop-blur-[2px]"
      />

      <Surface
        tone="paper"
        radius="md"
        border="hairline"
        shadow="pill"
        className="relative flex w-full max-w-[460px] flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-3.5">
          <Text variant="h4" as="h2">
            {t("shortcuts.title")}
          </Text>
          <span className="flex items-center gap-1.5 text-[11px] leading-none text-ink-faint">
            <Kbd>esc</Kbd>
            <span className="leading-none">{t("shortcuts.close")}</span>
          </span>
        </div>

        <ul className="flex max-h-[60vh] flex-col gap-0.5 overflow-y-auto p-2">
          {hints.map((hint) => (
            <li
              key={hint.shortcut}
              className="flex items-center justify-between gap-4 rounded-sm px-3 py-2"
            >
              <Text variant="body" as="span" className="min-w-0 flex-1 truncate text-ink-soft">
                {hint.label}
              </Text>
              <span className="flex shrink-0 gap-1">
                {(hint.chord ? formatChord(hint.chord) : formatShortcut(hint.shortcut ?? "")).map(
                  (token, i) => (
                    <Kbd key={i}>{token}</Kbd>
                  ),
                )}
              </span>
            </li>
          ))}
        </ul>
      </Surface>
    </div>,
    document.body,
  );
}
